import React, { useEffect, useState } from 'react';
import { CalendarClock, CalendarX2, Phone, Video } from 'lucide-react';

import { cn } from '../../../../lib/utils';
import { EYEBROW, SIDE_CARD } from './SideCards';

// Their next call (in-app meetings, Nic 2026-10-25): when it is (in their
// own clock), phone or video, and how to change it. A video meeting gets a
// Join button from 15 minutes before; a phone call says who calls them.
// Rescheduling goes through their specialist, never a link. A meeting we
// canceled stays on the card, said plainly, until a new one is booked (Nic
// 2026-10-25: good service, never just vanishing).

const formatPhone = (p) => {
  const d = String(p || '').replace(/\D/g, '').slice(-10);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : p || '';
};

const whenText = (at) => {
  const d = new Date(at);
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
  if (d.toDateString() === today.toDateString()) return { day: 'Today', time };
  if (d.toDateString() === tomorrow.toDateString()) return { day: 'Tomorrow', time };
  return { day: d.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' }), time };
};

const JOIN_EARLY_MS = 15 * 60000;

const MeetingCard = ({ meeting }) => {
  // Re-render each half minute so the Join button opens on time.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);
  if (!meeting) return null;
  const host = meeting.host || {};
  const first = host.userFirstName || 'Your specialist';
  const video = meeting.kind === 'video' && !!meeting.videoUrl;
  const start = new Date(meeting.startTime).getTime();
  const end = new Date(meeting.endTime || meeting.startTime).getTime();
  const live = now >= start - JOIN_EARLY_MS && now <= end + 30 * 60000;
  const { day, time } = whenText(meeting.startTime);
  const Icon = video ? Video : Phone;
  const canceled = meeting.status === 'canceled';
  const contact = (
    <>
      {first}
      {host.internalUserPhone ? (
        <>
          {' '}at{' '}
          {/* Plain text (Nic 2026-10-25): calling is the specialist card's
              button, on a phone. */}
          <span className="whitespace-nowrap font-semibold text-slate-700 dark:text-slate-200">
            {formatPhone(host.internalUserPhone)}
          </span>
        </>
      ) : null}
    </>
  );

  if (canceled) {
    return (
      <section className={SIDE_CARD}>
        <div className={EYEBROW}>Your call</div>
        <div className="mt-3 flex items-start gap-3">
          {/* Calm, never alarming (Nic 2026-10-25): grey, not red. */}
          <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-500 dark:!bg-white/10 dark:text-slate-300">
            <CalendarX2 size={20} />
          </span>
          <div className="min-w-0">
            <div className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600 dark:!bg-white/10 dark:text-slate-300">
              Canceled
            </div>
            {/* The strike in the text's own color, so it reads as one line. */}
            <div className="mt-1 text-sm font-semibold text-slate-500 line-through decoration-slate-500 decoration-1 dark:text-slate-400 dark:decoration-slate-400">
              {day} · {time}
            </div>
            <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              {meeting.kind === 'video' ? 'Video meeting' : 'Phone call'} with {first}
            </div>
          </div>
        </div>
        <div className="mt-4 rounded-xl bg-slate-50 px-3.5 py-2.5 text-sm text-slate-600 dark:!bg-white/5 dark:text-slate-300">
          Want a new time? Call or text {contact}.
        </div>
      </section>
    );
  }

  return (
    <section className={cn(SIDE_CARD, 'ring-1 ring-sky-200 dark:ring-sky-400/30')}>
      <div className={EYEBROW}>Your next call</div>
      <div className="mt-3 flex items-start gap-3">
        <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-600 dark:!bg-sky-500/15 dark:text-sky-300">
          <CalendarClock size={20} />
        </span>
        <div className="min-w-0">
          <div className="text-base font-bold text-slate-900 dark:text-white">{day}</div>
          <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">{time}</div>
          <div className="mt-0.5 inline-flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
            <Icon size={13} /> {video ? 'Video meeting' : 'Phone call'} with {first} · 30 minutes
          </div>
        </div>
      </div>

      <div className="mt-4">
        {video ? (
          live ? (
            <a
              href={meeting.videoUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-sky-600 px-4 text-sm font-semibold text-white no-underline hover:bg-sky-500 hover:text-white"
            >
              <Video size={16} /> Join video meeting
            </a>
          ) : (
            <div className="rounded-xl bg-slate-50 px-3.5 py-2.5 text-sm text-slate-600 dark:!bg-white/5 dark:text-slate-300">
              The <b>Join</b> button shows here 15 minutes before it starts.
            </div>
          )
        ) : (
          <div className="rounded-xl bg-slate-50 px-3.5 py-2.5 text-sm text-slate-600 dark:!bg-white/5 dark:text-slate-300">
            {first} will call you. Keep your phone close.
          </div>
        )}
      </div>

      <div className="mt-3 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
        Need a different time? Call or text {contact}.
      </div>
    </section>
  );
};

export default MeetingCard;
